import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Gantt } from '@bryntum/gantt';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useSharedResourcesStore } from 'store/sharedResources.store';

import { getActiveFilterCount } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { getSharedResourcesClusters } from 'components/common/bryntumGantt/helper';

export const useSharedResourcesAvailabilityLogic = ({ ui, api, globalFilterData }) => {
    const ref = useRef<HTMLDivElement>(null);
    const ganttInstance = useRef<Gantt | null>(null);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const isAnyGlobalFilterApplied = useIsAnyGlobalFilterApplied(globalFilterData);

    //region [[cluster logic]]
    const ganttData = api.getSharedResourcesAvailability.instance.data;
    const clustersList = useMemo(() => getSharedResourcesClusters(ganttData), [ganttData]);
    const clusterFromStore = useSharedResourcesStore((store) => store.cluster);

    const selectedCluster = useMemo(
        () => ganttData?.clusters?.find((item) => item.clusterId === Number(ui.state.cluster?.value)) || undefined,
        [ganttData, ui.state.cluster],
    );

    const indexOfSelectedClusterId =
        clustersList && clustersList.findIndex((item) => item.value === selectedCluster?.clusterId) + 1;
    const selectedClusterCountOfTotalTitle = ` (${indexOfSelectedClusterId} of ${clustersList?.length}): `;

    useEffect(() => {
        //initialize cluster state to store persistent value or set the first one from list
        const isPageView = !ui.isInPinboard;

        if (isPageView && clustersList && clustersList.length > 0) {
            const foundSelectedClusterFromPersistentStore = clustersList.find(
                (item) => item.value === Number(clusterFromStore?.value),
            );
            foundSelectedClusterFromPersistentStore
                ? ui.setters.setCluster(foundSelectedClusterFromPersistentStore)
                : ui.setters.setCluster(clustersList[0]);
        }
    }, [clustersList]);
    //endregion

    useEffect(() => {
        const handleFullScreen = () => {
            if (document.fullscreenElement) {
                setIsFullScreen(true);
            } else {
                setIsFullScreen(false);
            }
        };

        document.addEventListener('fullscreenchange', handleFullScreen);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreen);
        };
    }, []);

    const handleFullscreen = () => {
        if (ref.current) {
            if (!document.fullscreenElement) {
                return ref.current.requestFullscreen();
            } else if (document.exitFullscreen) {
                return document.exitFullscreen();
            }
        }
    };

    const getInstance = useCallback((gantt) => {
        if (gantt) {
            ganttInstance.current = gantt;
        }
    }, []);

    const zoomToFit = () => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.zoomToFit();
        }
    };

    return {
        logic: {
            ref,
            isFullScreen,
            handleFullscreen,
            getInstance,
            zoomToFit,
            clustersList,
            selectedCluster,
            selectedClusterCountOfTotalTitle,
            isAnyGlobalFilterApplied,
        },
    };
};

const useIsAnyGlobalFilterApplied = (globalFilterData) => {
    const defaultValues = useGlobalFilterDataStore((store) => store.defaultValues);
    const activeFilterCount = getActiveFilterCount(globalFilterData, Object.keys(defaultValues));
    const isAnyGlobalFilterApplied = activeFilterCount > 0;

    return isAnyGlobalFilterApplied;
};
